import { filter, find, map, round, toUpper } from "lodash";
import React from "react";
import { Person } from "@/features/people/graphql/generated";
import { DimText } from "@/shared/services/api/graphql/generated";
import { Card } from "@/shared/ui/Card";
import { Center, Column, Row } from "@/shared/ui/Blocks";
import { DimensionCard } from "@/shared/widgets/DimensionCard";
import { HighlightedWrapper } from "@/shared/ui/HighlightedWrapper";
import { Icon, IconName } from "@/shared/ui/Icon";
import { PeopleCompareScoreChart } from "@/shared/charts/PeopleCompareScoreChart";
import { SubtitleText } from "@/shared/ui/Typography";
import Chip from "@/shared/ui/Chip";
import IconTag from "@/shared/ui/IconTag";
import MarkdownText from "@/shared/widgets/MarkdownText";
import { useDimensionTextSelectors } from "@/shared/hooks/content/useDimensionTextSelectors";
import {
  DimensionWithScores,
  getTopStrengthDimensions,
} from "@/features/people/hooks/useDimensionsWithScores";
import { DimensionContext } from "@/types/data";
import { cn } from "@/shared/utils/shadcn";
import { CompetencyInsight } from "@/shared/hooks/insights/useCompetencyInsights";
import { useTranslation } from "react-i18next";
import { DimensionIcons } from "@/shared/constants";

export interface Tag {
  label: string;
  color: string;
  icon: IconName;
}

export interface DimensionDetailsContentProps {
  allDimensions: DimensionWithScores[];
  dimension: DimensionWithScores;
  context: DimensionContext;
  tags?: Tag[];
  insight?: CompetencyInsight;
  showHeader?: boolean;
  showSubDimensions?: boolean;
  className?: string;
}

interface TextSectionProps {
  title: string;
  texts?: DimText[];
  data?: { dimensions: DimensionWithScores[]; people: Person[] };
}

function TextSection(props: TextSectionProps) {
  const { title, texts, data } = props;

  if (!texts?.length) return null;

  return (
    <Column className="border-t first:mt-0 first:pt-0 first:border-t-0 border-grey-300 border-solid pt-4 mt-4 break-inside-avoid">
      <MarkdownText text={title} data={{ ...data, forceGrouping: false }} />
      <ul className="list-disc list-inside ml-2 mt-2 text-sm text-grey-700">
        {texts.map((text) => (
          <li key={text.text}>{text.text}</li>
        ))}
      </ul>
    </Column>
  );
}

function DimensionHeader(props: {
  dimension: DimensionWithScores;
  tags?: Tag[];
}) {
  const { dimension, tags } = props;
  const icon = DimensionIcons[
    dimension.id as keyof typeof DimensionIcons
  ] as IconName;

  return (
    <Row className="items-start gap-2 break-inside-avoid">
      <Column className="gap-4 flex-1">
        <Row className="items-center gap-2">
          {icon && <Icon icon={icon} color="black" />}
          <h5>{dimension.title}</h5>
          {dimension.score?.value != null && (
            <Center className="self-center rounded-full aspect-square h-7 bg-gray-900">
              <span className="text-sm font-semibold text-white">
                {round(dimension.score?.value || 0)}
              </span>
            </Center>
          )}
        </Row>
        <SubtitleText className={cn("print:text-xs", icon && "ml-[32px]")}>
          {dimension.description || ""}
        </SubtitleText>
      </Column>
      <Row className="items-center gap-4 justify-end flex-wrap">
        {!!dimension?.alignment?.label && (
          <Chip color={dimension.alignment?.color} variant="solid" size="sm">
            <p className="text-white text-xs">
              {toUpper(dimension.alignment?.label || "")}
            </p>
          </Chip>
        )}
        {!!tags &&
          map(tags, (tag) => (
            <IconTag
              key={tag.label}
              label={tag.label}
              color={tag.color}
              icon={tag.icon as IconName}
            />
          ))}
      </Row>
    </Row>
  );
}

function DimensionDetailsContent(props: DimensionDetailsContentProps) {
  const {
    allDimensions,
    dimension,
    context,
    tags,
    insight,
    showHeader = true,
    showSubDimensions = true,
    className,
  } = props;

  const getSubDimensions = (parent: number) =>
    filter(allDimensions, (dimension) => dimension.parentId === parent);
  const subDimensions = getSubDimensions(dimension.id || 0);

  const topStrengthDimensions = getTopStrengthDimensions(
    dimension,
    subDimensions,
    context
  );

  const {
    insightsDescription,
    actionTexts,
    strengthTexts,
    riskTexts,
    strengthTitle,
    riskTitle,
    actionTitle,
    hasInsightTexts,
    behaviorTitle,
    behaviorTexts,
  } = useDimensionTextSelectors(dimension, subDimensions, context);

  const people = map(
    dimension.peopleScores,
    (score) => score?.person
  ) as Person[];

  const { t } = useTranslation();

  return (
    <Column className={cn("gap-4", className)}>
      <Column className="gap-4">
        {showHeader && <DimensionHeader dimension={dimension} tags={tags} />}

        {dimension.score?.value != null && (
          <Column className="gap-4 border-y border-grey-300 border-solid py-8 mb-8 mt-4 print:mb-4 print:py-4 print:mt-0">
            <MarkdownText
              text={insightsDescription || ""}
              data={{ dimensions: subDimensions, people, forceGrouping: false }}
            />
          </Column>
        )}
      </Column>

      {dimension.score?.value != null && (
        <Card className="p-8 pt-4 shadow-xl mb-8 overflow-visible break-inside-avoid print:shadow-none print:mb-4 print:pt-0">
          <PeopleCompareScoreChart
            scores={dimension.peopleScores as any}
            extremes={dimension.extremes}
            averageScore={dimension?.averageScore?.value || 0}
            showArea={["team", "user_in_team", "candidate"].includes(context)}
            areaColor={dimension.alignment?.color || "yellow"}
            minScore={find(dimension?.allScores, { key: "min" })?.value || 0}
            maxScore={find(dimension?.allScores, { key: "max" })?.value || 0}
            highlightedPersonId={dimension.score?.obj_id || undefined}
          />
        </Card>
      )}

      {!!insight?.highlight && (
        <Card
          className={cn(
            insight.highlight?.color,
            "p-8 shadow-none mb-8 overflow-visible break-inside-avoid"
          )}
        >
          <Row className="items-center gap-4 justify-between mb-2">
            <p className="mb-2 !text-md font-semibold">
              {insight.highlight?.title}
            </p>
            <IconTag
              label={insight.highlight?.tag?.label}
              color={insight.highlight?.tag?.color}
              icon={insight.highlight?.tag?.icon as IconName}
            />
          </Row>
          <MarkdownText
            className="!text-sm"
            text={insight.highlight?.description || ""}
            data={{ dimensions: subDimensions, people, forceGrouping: false }}
          />
          {insight.highlight?.questions && (
            <ul className="list-disc list-inside mt-2 text-sm text-grey-700">
              <p className="mb-2 text-sm font-semibold">
                {t("competency_insights.interview_questions")}
              </p>
              {insight.highlight?.questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {hasInsightTexts && (
        <Column className="border-y bg-grey-100 border-grey-300 print:bg-transparent border-solid py-8 mt-4 print:mt-0 -mx-12 print:mx-0 p-12 print:p-0 print:py-4">
          <TextSection
            title={strengthTitle}
            texts={strengthTexts}
            data={{ dimensions: subDimensions, people }}
          />
          <TextSection
            title={behaviorTitle}
            texts={behaviorTexts}
            data={{ dimensions: subDimensions, people }}
          />
          <TextSection
            title={riskTitle}
            texts={riskTexts}
            data={{ dimensions: subDimensions, people }}
          />
          {/* <TextSection
          title={actionTitle}
          texts={actionTexts as DimText[]}
          data={{ dimensions: subDimensions, people }}
        /> */}
        </Column>
      )}

      {showSubDimensions && (
        <Row className="grid gap-8 grid-cols-2 mt-8">
          {subDimensions.length > 0 &&
            map(subDimensions, (subDimension) => (
              <HighlightedWrapper
                key={subDimension.id}
                isHighlighted={topStrengthDimensions.some(
                  (d) => d.id === subDimension.id
                )}
                highlightedLabel={t("dimensions.most_impactful")}
              >
                <Card className="p-4 print:shadow-none">
                  <DimensionCard
                    id={subDimension.id}
                    title={subDimension.title}
                    description={subDimension.description}
                    scores={subDimension.peopleScores as any}
                    score={subDimension.score}
                    averageScore={subDimension.averageScore}
                    showScore
                    showAverageScore={context !== "user" && context !== "team"}
                  />
                </Card>
              </HighlightedWrapper>
            ))}
        </Row>
      )}
    </Column>
  );
}

export default DimensionDetailsContent;
